/**
 * Grid cell component
 * Individual cell display and details
 */

import { Card, Space, Typography, Tag, Descriptions, Button, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
  EnvironmentOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';
import type { GridCell as GridCellType, GridCellStatus } from '@/types/grid';
import { CELL_STATUS_CONFIG } from '@/utils/constants';
import { formatNumber, formatDuration, formatDate } from '@/utils/formatters';

const { Text, Paragraph } = Typography;

interface GridCellProps {
  cell: GridCellType;
  onRetry?: () => void;
  onViewResults?: () => void;
  loading?: boolean;
}

const statusIcons: Record<GridCellStatus, React.ReactNode> = {
  pending: <ClockCircleOutlined />,
  queued: <ClockCircleOutlined />,
  processing: <SyncOutlined spin />,
  completed: <CheckCircleOutlined />,
  failed: <CloseCircleOutlined />,
  skipped: <MinusCircleOutlined />,
};

export function GridCellCard({
  cell,
  onRetry,
  onViewResults,
  loading,
}: GridCellProps) {
  const config = CELL_STATUS_CONFIG[cell.status];

  return (
    <Card
      size="small"
      title={
        <Space>
          <span>Cell {cell.index !== undefined ? cell.index + 1 : cell.id}</span>
          <Tag
            icon={statusIcons[cell.status]}
            color={config.color}
          >
            {config.label}
          </Tag>
        </Space>
      }
      extra={
        <Space size={4}>
          {cell.status === 'failed' && onRetry && (
            <Tooltip title="Retry this cell">
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={onRetry}
                loading={loading}
              />
            </Tooltip>
          )}
          {cell.status === 'completed' && cell.placesFound > 0 && onViewResults && (
            <Tooltip title="View results">
              <Button
                size="small"
                icon={<EnvironmentOutlined />}
                onClick={onViewResults}
              />
            </Tooltip>
          )}
        </Space>
      }
    >
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {/* Bounds info */}
        <Descriptions size="small" column={2}>
          <Descriptions.Item label="North">
            {cell.bounds.north.toFixed(6)}
          </Descriptions.Item>
          <Descriptions.Item label="South">
            {cell.bounds.south.toFixed(6)}
          </Descriptions.Item>
          <Descriptions.Item label="East">
            {cell.bounds.east.toFixed(6)}
          </Descriptions.Item>
          <Descriptions.Item label="West">
            {cell.bounds.west.toFixed(6)}
          </Descriptions.Item>
        </Descriptions>

        {/* Results */}
        {cell.placesFound !== undefined && cell.placesFound >= 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <EnvironmentOutlined style={{ color: config.color }} />
            <Text>{formatNumber(cell.placesFound)} places found</Text>
          </div>
        )}

        {/* Duration */}
        {cell.duration && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FieldTimeOutlined style={{ color: 'var(--color-text-tertiary)' }} />
            <Text type="secondary">{formatDuration(cell.duration)}</Text>
          </div>
        )}

        {/* Error message */}
        {cell.error && (
          <Paragraph
            type="danger"
            style={{
              margin: 0,
              padding: 8,
              background: 'var(--color-error-bg)',
              borderRadius: 4,
              fontSize: 12,
            }}
            ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}
          >
            {cell.error}
          </Paragraph>
        )}

        {/* Timestamps */}
        {(cell.startedAt || cell.completedAt) && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            {cell.startedAt && `Started: ${formatDate(cell.startedAt, 'time')}`}
            {cell.startedAt && cell.completedAt && ' | '}
            {cell.completedAt && `Completed: ${formatDate(cell.completedAt, 'time')}`}
          </Text>
        )}
      </Space>
    </Card>
  );
}

/**
 * Compact cell badge
 */
export function GridCellBadge({
  cell,
  onClick,
  size = 'default',
}: {
  cell: GridCellType;
  onClick?: () => void;
  size?: 'small' | 'default';
}) {
  const config = CELL_STATUS_CONFIG[cell.status];

  const sizeStyles = size === 'small'
    ? { width: 24, height: 24, fontSize: 10 }
    : { width: 32, height: 32, fontSize: 12 };

  return (
    <Tooltip
      title={
        <div>
          <div>Cell {cell.index !== undefined ? cell.index + 1 : cell.id}</div>
          <div>Status: {config.label}</div>
          {cell.placesFound !== undefined && <div>Places: {cell.placesFound}</div>}
        </div>
      }
    >
      <div
        onClick={onClick}
        style={{
          ...sizeStyles,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: config.bgColor,
          color: config.color,
          border: `1px solid ${config.borderColor}`,
          borderRadius: 4,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'transform 0.1s',
        }}
        onMouseOver={(e) => {
          if (onClick) {
            e.currentTarget.style.transform = 'scale(1.1)';
          }
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        {cell.index !== undefined ? cell.index + 1 : '#'}
      </div>
    </Tooltip>
  );
}

/**
 * Cell status inline
 */
export function GridCellStatus({ status }: { status: GridCellStatus }) {
  const config = CELL_STATUS_CONFIG[status];

  return (
    <Tag
      icon={statusIcons[status]}
      color={config.color}
      style={{ margin: 0 }}
    >
      {config.label}
    </Tag>
  );
}

export default GridCellCard;
